import {useGlobalState} from "../../../GlobalState";
import {CANCELLATION_POLICIES, TOURIST_TAX_POLICIES} from "../../../services/booking_conditions/enums";
import {isValidDateObject} from "../../../services/date/validators";
import {startOfDay} from "date-fns";
import useAccommodationUnitSettings from "../../../hooks/useAccommodationUnitSettings";


const useBookingConditionUtils = ({booking}) => {
    const {settings} = useGlobalState();

    const {getCheckInFrom, getCheckOutBefore} = useAccommodationUnitSettings();

    const defaultCancellationPolicy = settings?.defaultCancellationPolicy ?? CANCELLATION_POLICIES.NONE;
    const defaultTouristTaxPolicy = settings?.defaultTouristTaxPolicy ?? TOURIST_TAX_POLICIES.NONE;
    const defaultTouristTaxUnitPrice = settings?.defaultTouristTaxUnitPrice ?? 1.5;
    const defaultForewarningForRefundInDays = settings?.defaultForewarningForRefundInDays ?? 60;

    const accommodationUnitIds = booking.bookingItems?.map(bi => bi.bookableId) ?? [];
    const defaultCheckInFrom = getCheckInFrom({accommodationUnitIds});
    const defaultCheckOutBefore = getCheckOutBefore({accommodationUnitIds});

    // Function to compute the refundable until date based on arrival date and the cancellation policy.
    // If the cancellation policy is time restricted refundable compute arrival - defaultForewarningForRefundInDays
    // else return null.
    const getRefundableUntilDate = ({arrival, cancellationPolicy}) => {
        if (cancellationPolicy === CANCELLATION_POLICIES.TIME_RESTRICTED_REFUNDABLE) {
            if (isValidDateObject(arrival) && defaultForewarningForRefundInDays > 0) {
                let refundableUntilDate = startOfDay(new Date(arrival));
                refundableUntilDate.setDate(refundableUntilDate.getDate() - defaultForewarningForRefundInDays);
                return refundableUntilDate;
            }
        }
        return null;
    }

    // Function to determine if the cancellation policy should be forced to not refundable
    // based on the refundable until date. If the cancellation policy is time restricted refundable and the
    // refundable until date is in the past return true, else return false.
    const forceNotRefundable = ({arrival, cancellationPolicy}) => {
        if (cancellationPolicy === CANCELLATION_POLICIES.TIME_RESTRICTED_REFUNDABLE) {
            const refundableUntilDate = getRefundableUntilDate({arrival, cancellationPolicy});
            if (refundableUntilDate && refundableUntilDate < new Date()) {
                return true;
            }
        }
        return false;
    }

    // Compute initial cancellation policy
    let initialCancellationPolicy = booking.bookingCondition?.cancellationPolicy ?? defaultCancellationPolicy;
    if (forceNotRefundable({arrival: booking.getArrival(), cancellationPolicy: initialCancellationPolicy})) {
        initialCancellationPolicy = CANCELLATION_POLICIES.NOT_REFUNDABLE;
    }

    // Compute initial refundable until date
    let initialRefundableUntil = booking.bookingCondition?.refundableUntil ?? getRefundableUntilDate({arrival: booking.getArrival(), cancellationPolicy: initialCancellationPolicy});
    if(initialRefundableUntil && initialRefundableUntil < new Date()){
        initialRefundableUntil = null;
    }

    const getTouristTaxUnitPrice = ({touristTaxPolicy, touristTaxUnitPrice}) => {
        if (touristTaxPolicy === TOURIST_TAX_POLICIES.EXCLUDED || touristTaxPolicy === TOURIST_TAX_POLICIES.INCLUDED) {
            if (isInputBlank(touristTaxUnitPrice)) {
                return defaultTouristTaxUnitPrice;
            } else {
                return touristTaxUnitPrice;
            }
        } else {
            return null;
        }
    }

    const initialTouristTaxUnitPrice = getTouristTaxUnitPrice({
        touristTaxPolicy: defaultTouristTaxPolicy,
        touristTaxUnitPrice: defaultTouristTaxUnitPrice
    });

    // Initial formik state for booking condition
    const initialBookingCondition = {
        cancellationPolicy: initialCancellationPolicy,
        touristTaxPolicy: booking.bookingCondition?.touristTaxPolicy ?? defaultTouristTaxPolicy,
        refundableUntil: booking.bookingCondition?.refundableUntil ?? initialRefundableUntil,
        touristTaxUnitPrice: initialTouristTaxUnitPrice,
        checkInFrom: booking.bookingCondition?.checkInFrom ?? defaultCheckInFrom,
        checkOutBefore: booking.bookingCondition?.checkOutBefore ?? defaultCheckOutBefore,
    }

    // Function to set the default RefundableUntil field based on the cancellationPolicy
    // If the cancellationPolicy is different from time restricted refundable set refundableUntil to null,
    // else if refundableUntil is blank set it to arrival - defaultForewarningForRefundInDays, if the
    // computed refundableUntilDate is in the past leave it null.
    const setDefaultRefundableUntil = ({formik, cancellationPolicy, arrival}) => {
        const refundableUntilDate = getRefundableUntilDate({arrival, cancellationPolicy});
        if (refundableUntilDate && refundableUntilDate > new Date()) {
            formik.setFieldValue('bookingCondition.refundableUntil', refundableUntilDate);
        } else {
            formik.setFieldValue('bookingCondition.refundableUntil', null);
        }
    }

    const handleCancellationPolicyChange = ({formik, cancellationPolicy, arrival}) => {
        formik.setFieldValue('bookingCondition.cancellationPolicy', cancellationPolicy);
        setDefaultRefundableUntil({formik, cancellationPolicy, arrival});
    }

    // Handler for the change of cancellation policy. It set the default refundable until if needed.
    const cancellationPolicyChangeHandler = (formik) => {
        return (e) => {
            const cancellationPolicy = e.target.value;
            const arrival = formik.values.arrival;
            handleCancellationPolicyChange({formik, cancellationPolicy, arrival});
        }
    }

    // Handler for the change of arrival date. It set the default cancellation policy and refundable until if needed.
    const updateAfterArrivalChangeHandler = (formik) => {
        return (arrival) => {
            if(isValidDateObject(arrival)){
                // Reset cancellation policy to default value
                const cancellationPolicy = defaultCancellationPolicy;
                if(forceNotRefundable({arrival, cancellationPolicy})){
                    handleCancellationPolicyChange({formik, cancellationPolicy: CANCELLATION_POLICIES.NOT_REFUNDABLE, arrival});
                } else{
                    handleCancellationPolicyChange({formik, cancellationPolicy, arrival});
                }
            }
        }
    }

    const updateAfterBookingItemsChangeHandler = (formik) => {
        return (accommodationUnits) => {
            const accommodationUnitIds = accommodationUnits?.map(accommodation_unit => accommodation_unit.id) ?? [];
            const newCheckInFrom = getCheckInFrom({accommodationUnitIds});
            const newCheckOutBefore = getCheckOutBefore({accommodationUnitIds});
            formik.setFieldValue('bookingCondition.checkInFrom', newCheckInFrom);
            formik.setFieldValue('bookingCondition.checkOutBefore', newCheckOutBefore);
        }
    }

    // Given a formik object return a handler for the change of tourist tax policy. It set the default unit price if needed.
    const touristTaxPolicyChangeHandler = (formik) => {
        return (e) => {
            const value = e.target.value;
            formik.setFieldValue('bookingCondition.touristTaxPolicy', value);
            const touristTaxUnitPrice = getTouristTaxUnitPrice({
                touristTaxPolicy: value,
                touristTaxUnitPrice: formik.values.bookingCondition.touristTaxUnitPrice
            });
            formik.setFieldValue('bookingCondition.touristTaxUnitPrice', touristTaxUnitPrice);
        }
    }

    // Disabled states for fields depending on other fields
    const touristTaxUnitPriceDisabled = (touristTaxPolicy) => !(touristTaxPolicy === TOURIST_TAX_POLICIES.EXCLUDED || touristTaxPolicy === TOURIST_TAX_POLICIES.INCLUDED);
    const refundableUntilDisabled = (cancellationPolicy) => cancellationPolicy !== CANCELLATION_POLICIES.TIME_RESTRICTED_REFUNDABLE;

    return {
        defaultTouristTaxUnitPrice,
        defaultForewarningForRefundInDays,
        initialBookingCondition,
        touristTaxPolicyChangeHandler,
        cancellationPolicyChangeHandler,
        updateAfterArrivalChangeHandler,
        updateAfterBookingItemsChangeHandler,
        touristTaxUnitPriceDisabled,
        refundableUntilDisabled
    }
}

const isInputBlank = (v) => v === null || v === undefined || v === '';

export default useBookingConditionUtils;